import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message, Spin } from 'antd-v5';
import PageHeaderLayout from '@/component/page-header-layout';
import { Button, Card, Checkbox, Form, Input } from '@/components/index';
import {
  addAdminRoleRequest,
  getAdminRoleListRequest,
  getAdminRolePermissionListRequest,
  updateAdminRoleRequest,
} from '@/state/request/adminUser';
import './style.less';

const ROLE_LIST_PATH = '/system/roleManagement';
const trimValue = (value) => (typeof value === 'string' ? value.trim() : value);
const getRoleListLocation = (search) => ({
  pathname: ROLE_LIST_PATH,
  search: search || '',
});

const SystemRoleEdit = ({ history, match, location }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permissionList, setPermissionList] = useState([]);
  const roleId = match?.params?.roleId;
  const isEdit = !!roleId;

  const getPermissionList = useCallback(async () => {
    const res = await getAdminRolePermissionListRequest();
    if (res?.code === 200) {
      setPermissionList(Array.isArray(res.data) ? res.data : []);
    }
  }, []);

  const getRoleDetail = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const res = await getAdminRoleListRequest();
      if (res?.code === 200) {
        const list = Array.isArray(res.data) ? res.data : [];
        const record = list.find((item) => String(item.roleId) === String(roleId));
        if (record) {
          form.setFieldsValue({
            remark: record.remark,
            key: record.key,
            permissionCodeList: record.permissionCodeList || [],
          });
        } else {
          message.error('角色不存在');
          history.push(getRoleListLocation(location?.search));
        }
      }
    } finally {
      setLoading(false);
    }
  }, [form, history, isEdit, location?.search, roleId]);

  useEffect(() => {
    getPermissionList();
    getRoleDetail();
  }, [getPermissionList, getRoleDetail]);

  const handleCancel = () => {
    history.push(getRoleListLocation(location?.search));
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        key: trimValue(values.key),
        remark: trimValue(values.remark),
        permissionCodeList: values.permissionCodeList || [],
      };
      const res = isEdit
        ? await updateAdminRoleRequest({ roleId: Number(roleId), ...payload })
        : await addAdminRoleRequest(payload);
      if (res?.code === 200) {
        message.success(isEdit ? '编辑成功' : '新增成功');
        history.push(getRoleListLocation(location?.search));
      }
    } finally {
      setSaving(false);
    }
  };

  const permissionOptions = useMemo(
    () =>
      permissionList.map((item) => ({
        label: item.name,
        value: item.code,
      })),
    [permissionList],
  );

  return (
    <PageHeaderLayout>
      <Spin spinning={loading}>
        <Form form={form} className='system-role-edit-form' layout='vertical'>
          <div className='system-role-edit'>
            <Card bordered={false} className='system-role-base-card'>
              <div className='system-role-base-form'>
                <Form.Item
                  label='角色名称'
                  name='remark'
                  normalize={trimValue}
                  rules={[
                    { required: true, message: '请输入角色名称' },
                    { max: 32, message: '角色名称最多32个字符' },
                  ]}
                >
                  <Input placeholder='请输入角色名称' maxLength={32} showCount />
                </Form.Item>
                <Form.Item
                  label='Key'
                  name='key'
                  normalize={trimValue}
                  rules={[
                    { required: true, message: '请输入Key' },
                    { max: 32, message: 'Key最多32个字符' },
                  ]}
                >
                  <Input placeholder='请输入Key' maxLength={32} showCount />
                </Form.Item>
              </div>
            </Card>
            <Card bordered={false} className='system-role-permission-card'>
              <div className='system-role-section-title'>权限</div>
              <div className='system-role-permission-form'>
                <Form.Item
                  name='permissionCodeList'
                  rules={[{ required: true, type: 'array', min: 1, message: '请选择权限' }]}
                >
                  <Checkbox.Group className='system-role-permission-group'>
                    {permissionOptions.map((item) => (
                      <div className='system-role-permission-item' key={item.value}>
                        <Checkbox value={item.value}>{item.label}</Checkbox>
                      </div>
                    ))}
                  </Checkbox.Group>
                </Form.Item>
              </div>
            </Card>
            <div className='system-role-edit-footer'>
              <Button onClick={handleCancel}>取消</Button>
              <Button type='primary' loading={saving} onClick={handleSave}>
                保存
              </Button>
            </div>
          </div>
        </Form>
      </Spin>
    </PageHeaderLayout>
  );
};

export default SystemRoleEdit;
